import { cn, Spinner } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { ContactDetailsBlockContent, CustomizeHelperBlock } from "../blocks";
import { useCallback, useMemo, useRef, useState } from "react";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { usePermissionQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { BlockProps } from "../../types";
import { withKey } from "@tellescope/ui";
import { useBlockAvailability, usePendingBlockOrder, useReorderBlocks } from "../../hooks";
import { useResolvedView } from "../../hooks/use-resolved-view";
import { getBlockSortableId, resolveBlockRef, type VisibleBlock } from "../../utils";
import { SortableBlockRow } from "./SortableBlockRow";
import { SortableBlockList } from "./SortableBlockList";
import { FlaggedNoteBanner } from "./FlaggedNoteBanner";
import { FlaggedNoteOverlay } from "./FlaggedNoteOverlay";

export interface BlockToHide {
  id?: string;
  index: number;
}

export interface ContactDetailsViewBodyProps {
  viewId: string;
  enduserId: string;
  className?: string;
  onMaximizeBlock: (ref: { blockId?: string; blockIndex: number }) => void;
  blocksToHide?: BlockToHide[];
  onChangeEnduser: BlockProps["onChangeEnduser"];
  isFeatureEnabled?: BlockProps["isFeatureEnabled"];
  onOpenTicket?: BlockProps["onOpenTicket"];
  onOpenEvent?: BlockProps["onOpenEvent"];
  canUpdateObservations: boolean;
  onViewMaterialized?: BlockProps["onViewMaterialized"];
}

export const ContactDetailsViewBody = withKey(
  ({
    viewId,
    enduserId,
    className,
    onMaximizeBlock,
    blocksToHide = [],
    onChangeEnduser,
    isFeatureEnabled,
    onOpenTicket,
    onOpenEvent,
    canUpdateObservations,
    onViewMaterialized,
  }: ContactDetailsViewBodyProps) => {
    const { t } = useTranslation("contacts");
    const [selectedBlockRef, setSelectedBlockRef] = useState<{ id?: string; index: number }>();
    // Selecting a block swaps this list out for the single-block view, so the scroll offset is kept
    // here and reapplied when the list mounts again.
    const listScrollTopRef = useRef(0);
    const setListRef = useCallback((node: HTMLElement | null) => {
      if (node) node.scrollTop = listScrollTopRef.current;
    }, []);

    const { data: flaggedNote } = useTellescopeQuery({
      model: "endusers",
      method: "getOne",
      request: enduserId,
      enabled: !!enduserId,
      select: (enduser) => (enduser.noteIsFlagged && enduser.note ? enduser.note : ""),
    });

    const [hasAcknowledgedNote, setHasAcknowledgedNote] = useState(false);

    const { isAvailable } = useBlockAvailability({ isFeatureEnabled });
    const resolvedView = useResolvedView(viewId);
    const blocks = resolvedView.mode === "loading" || resolvedView.mode === "error" ? undefined : resolvedView.blocks;
    const visibleBlocks = useMemo(() => {
      return (
        blocks?.reduce<{ block: NonNullable<typeof blocks>[number]; index: number }[]>((acc, curr, i) => {
          const isHidden = blocksToHide.some((hidden) =>
            hidden.id !== undefined ? curr.info?.id === hidden.id : i === hidden.index
          );
          if (isHidden || !isAvailable(curr.type)) {
            return acc;
          }

          acc.push({ block: curr, index: i });
          return acc;
        }, []) ?? []
      );
    }, [blocks, blocksToHide, isAvailable]);

    const { applyPendingOrder, setPendingOrder } = usePendingBlockOrder();
    const blocksToShow = applyPendingOrder(visibleBlocks);

    const {
      update: { access: canReorderBlocks },
    } = usePermissionQuery({
      model: "custom_dashboards",
      actions: ["update"],
      record: resolvedView.mode === "saved" ? resolvedView.view : undefined,
    });

    const reorderBlocks = useReorderBlocks({ viewId, onViewMaterialized });

    const onDragEnd = useCallback(
      ({ active, over }: DragEndEvent) => {
        if (!over) return;
        const activeId = String(active.id);
        const overId = String(over.id);

        const ids = blocksToShow.map(({ block, index }) => getBlockSortableId(block, index));
        const from = ids.indexOf(activeId);
        const to = ids.indexOf(overId);
        if (from !== -1 && to !== -1 && from !== to) {
          setPendingOrder(visibleBlocks, arrayMove(ids, from, to));
        }

        reorderBlocks({ visibleBlocks, activeId, overId });
      },
      [blocksToShow, reorderBlocks, setPendingOrder, visibleBlocks]
    );

    if (resolvedView.mode === "loading") {
      return (
        <section className={cn("flex items-center justify-center w-full h-full", className)}>
          <Spinner direction="column">{t("Loading...")}</Spinner>
        </section>
      );
    }

    if (resolvedView.mode === "error") {
      return (
        <section
          className={cn("flex items-center justify-center w-full h-full text-sm text-muted-foreground", className)}
        >
          {t("This view could not be loaded. Please try again.")}
        </section>
      );
    }

    if (selectedBlockRef !== undefined) {
      const { block: selectedBlock, index: resolvedIndex } = resolveBlockRef({
        blocks: resolvedView.blocks,
        blockId: selectedBlockRef.id,
        blockIndex: selectedBlockRef.index,
      });
      if (selectedBlock && isAvailable(selectedBlock.type)) {
        return (
          <section className={cn("p-4 w-full grow flex flex-col min-h-0", className)}>
            <ContactDetailsBlockContent
              block={selectedBlock}
              enduserId={enduserId}
              viewId={viewId}
              blockId={selectedBlock.info?.id}
              blockIndex={resolvedIndex}
              editMode
              setEditMode={(newVal) => {
                const calcVal = newVal instanceof Function ? newVal(true) : newVal;
                if (!calcVal) {
                  setSelectedBlockRef(undefined);
                }
              }}
              className="grow flex flex-col min-h-0"
              blockLayout="fill"
              onChangeEnduser={onChangeEnduser}
              isFeatureEnabled={isFeatureEnabled}
              canUpdateObservations={canUpdateObservations}
              onOpenTicket={onOpenTicket}
              onOpenEvent={onOpenEvent}
              onViewMaterialized={onViewMaterialized}
            />
          </section>
        );
      }
    }

    const blockContentProps = ({ block, index }: VisibleBlock) => ({
      block,
      enduserId,
      viewId,
      blockId: block.info?.id,
      blockIndex: index,
      onMaximize: () => onMaximizeBlock({ blockId: block.info?.id, blockIndex: index }),
      className: "border rounded-sm px-4 py-3",
      contentClassName: "max-h-[500px]",
      onChangeEnduser,
      isFeatureEnabled,
      canUpdateObservations,
      onOpenTicket,
      onOpenEvent,
      onViewMaterialized,
    });

    return (
      <div className={cn("relative flex min-h-0 w-full", className)}>
        <section
          ref={setListRef}
          onScroll={(e) => {
            listScrollTopRef.current = e.currentTarget.scrollTop;
          }}
          className="p-4 space-y-2 w-full overflow-y-auto"
        >
          <CustomizeHelperBlock
            viewId={viewId}
            isFeatureEnabled={isFeatureEnabled}
            onViewMaterialized={onViewMaterialized}
          />
          {!!flaggedNote && <FlaggedNoteBanner note={flaggedNote} />}
          {blocksToShow.length ? (
            <SortableBlockList
              visibleBlocks={blocksToShow}
              enabled={canReorderBlocks}
              onDragEnd={onDragEnd}
              renderOverlay={(visible) => <ContactDetailsBlockContent {...blockContentProps(visible)} />}
            >
              {blocksToShow.map(({ block, index }) => (
                <SortableBlockRow
                  key={getBlockSortableId(block, index)}
                  sortable={canReorderBlocks}
                  {...blockContentProps({ block, index })}
                  setEditMode={(newVal) => {
                    const calcVal = newVal instanceof Function ? newVal(false) : newVal;
                    if (calcVal) {
                      setSelectedBlockRef({ id: block.info?.id, index });
                    }
                  }}
                />
              ))}
            </SortableBlockList>
          ) : (
            <div className="flex flex-col h-full p-6 text-muted-foreground text-sm items-center">
              {blocksToHide.length ? t("No other blocks to display") : t("Add a block to get started")}
            </div>
          )}
        </section>
        {!!flaggedNote && !hasAcknowledgedNote && (
          <FlaggedNoteOverlay note={flaggedNote} onAcknowledge={() => setHasAcknowledgedNote(true)} />
        )}
      </div>
    );
  },
  ({ enduserId, viewId }) => `${enduserId}-${viewId}`
);
